import {
  Box,
  BoxProps,
  Chip,
  styled,
  Tab,
  TabProps,
  Tabs,
} from '@mui/material';

import { LinkProps } from 'react-router-dom';

import breakpoints from '../../../constants/breakpoints';
import { colorNeutral } from '../../../styles/mui/colors';

type StyledTabProps = TabProps & {
  'data-is-first'?: boolean;
  to: LinkProps['to'];
};

type PanelProps = BoxProps & {
  'data-is-first-selected'?: boolean;
};

export const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 'var(--border-radius-small)',
  color: theme.palette.neutral.dark,
  fontSize: 'var(--font-sm)',
  [theme.breakpoints.down(breakpoints.laptopS)]: {
    '& .MuiChip-icon': {
      margin: 0,
    },
    '& .MuiChip-label': {
      display: 'none',
    },
    minWidth: 'var(--spacing-large)',
  },
}));

export const StyledPanel = styled(Box)<PanelProps>(
  ({ theme, 'data-is-first-selected': isFirstTabSelected = false }) => ({
    backgroundColor: 'var(--color-background)',
    border: `1px solid ${theme.palette.neutral.light}`,
    borderRadius: 'var(--border-radius-medium)',
    marginTop: '-2px',
    paddingBlock: 'var(--spacing-medium)',
    ...(isFirstTabSelected && { borderTopLeftRadius: 0 }),
  }),
);

export const StyledTab = styled(Tab)<StyledTabProps>(
  ({ theme, 'data-is-first': isFirstTab = false }) => ({
    '& .MuiChip-root': {
      color: 'var(--color-gray-800)',
    },
    '&.Mui-selected': {
      '&:before, &:after': {
        border: `1px solid ${theme.palette.neutral.light}`,
        bottom: 0,
        content: '" "',
        height: 'var(--spacing-big)',
        position: 'absolute',
        width: 'var(--spacing-large)',
      },
      // Sorting will override styles above, and we don't want to
      // eslint-disable-next-line sort-keys
      '&:after': {
        borderBottomLeftRadius: 'var(--border-radius-medium)',
        borderWidth: '0 0 1px 1px',
        boxShadow:
          'calc(-1 * var(--spacing-small)) var(--spacing-tiny) 0 var(--color-white)',
        right: 'calc(-1 * var(--spacing-large))',
      },
      '&:before': {
        borderBottomRightRadius: 'var(--border-radius-medium)',
        borderWidth: '0 1px 1px 0',
        boxShadow:
          'var(--spacing-small) var(--spacing-tiny) 0 var(--color-white)',
        left: 'calc(-1 * var(--spacing-large))',
        ...(isFirstTab && { display: 'none' }),
      },
      backgroundColor: 'var(--color-background)',
      border: `1px solid ${theme.palette.neutral.light}`,
      borderBottomColor: 'var(--color-white)',
      borderTopLeftRadius: 'var(--border-radius-medium)',
      borderTopRightRadius: 'var(--border-radius-medium)',
      color: 'var(--color-gray-800)',
      overflow: 'visible',
      paddingLeft: 'var(--spacing-small)',
      paddingRight: 'var(--spacing-medium)',
      paddingTop: 'var(--spacing-tiny)',
      zIndex: 0,
    },
    '&:focus-visible': {
      '& .MuiChip-root': {
        backgroundColor: colorNeutral[400],
        borderColor: 'var(--color-grey-darkest)',
      },
      boxShadow: 'none',
    },
    '&:hover': {
      '& .MuiChip-root': {
        backgroundColor: colorNeutral[300],
      },
    },
    fontSize: 'var(--font-sm)',
    maxHeight: 'calc(var(--spacing-small) + var(--spacing-large))',
    minHeight: 'calc(var(--spacing-small) + var(--spacing-large))',
    minWidth: 'auto',
    padding: '0 0 var(--spacing-small)',
    position: 'relative',
    textTransform: 'inherit',
    zIndex: 'var(--z-1)',
  }),
);

export const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-flexContainer': {
    gap: 'var(--spacing-small)',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  maxHeight: 'calc(var(--spacing-small) + var(--spacing-large))',
  minHeight: 'calc(var(--spacing-small) + var(--spacing-large))',
}));
